import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2 } from "lucide-react";
import { useWeb3Form } from "../../hooks/useWeb3Form";

const ukPhone = /^(?:\+44\s?|0)\d[\d\s]{8,12}$/;

const LICENCE_TYPES = ["image/jpeg", "image/png", "image/heic", "image/heif", "application/pdf"];
const LICENCE_EXTS = [".jpg", ".jpeg", ".png", ".heic", ".heif", ".pdf"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB (FormSubmit free-tier limit)

function isAllowedLicenceFile(file) {
  if (!file) return false;
  if (LICENCE_TYPES.includes(file.type)) return true;
  // Some browsers report an empty/unknown MIME type for HEIC — fall back to extension.
  const name = (file.name || "").toLowerCase();
  return LICENCE_EXTS.some((ext) => name.endsWith(ext));
}

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name."),
  phone: z
    .string()
    .trim()
    .regex(ukPhone, "Please enter a valid UK mobile number (e.g. 07700 900000)."),
  email: z.string().trim().email("Please enter a valid email address."),
  essexArea: z.string().trim().min(2, "Please tell us your Essex home town or general area."),
  badgeNumber: z
    .string()
    .trim()
    .min(3, "Please enter your TfL Green Badge licence number."),
  licenceExpiry: z
    .string()
    .min(1, "Please enter your licence expiry date.")
    .refine((v) => {
      const exp = new Date(v + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return exp >= today;
    }, "Your licence must be current — the expiry date cannot be in the past."),
  licenceFile: z
    .any()
    .refine((files) => files && files.length > 0, "Please upload a photo or PDF of your current TfL taxi driver licence.")
    .refine((files) => !files?.[0] || isAllowedLicenceFile(files[0]), "Licence file must be a JPG, PNG, HEIC or PDF.")
    .refine((files) => !files?.[0] || files[0].size <= MAX_FILE_SIZE, "Licence file must be 10MB or smaller."),
  morningTimes: z.string().trim().min(2, "Please tell us your usual Essex → London travel times."),
  eveningTimes: z.string().trim().min(2, "Please tell us your usual London → Essex travel times."),
  londonAreas: z.string().trim().min(2, "Please tell us your preferred London working areas."),
  capacity: z.string().min(1, "Please select your vehicle passenger capacity."),
  terms: z.literal(true, {
    errorMap: () => ({ message: "Please agree to the driver referral terms to apply." }),
  }),
  privacy: z.literal(true, {
    errorMap: () => ({ message: "Please acknowledge the privacy notice to continue." }),
  }),
});

const inputCls =
  "w-full bg-transparent border border-[#27272A] rounded-sm p-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none";
const labelCls = "block text-sm uppercase tracking-[0.12em] font-semibold text-[#A1A1AA] mb-2";
const errCls = "mt-1.5 text-sm text-red-400";

function Field({ label, error, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls}>
        {label}
      </label>
      {children}
      {error && (
        <p role="alert" className={errCls} data-testid={`error-${htmlFor}`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function DriverForm() {
  const { status, errorMessage, submit } = useWeb3Form({
    subject: "NEEDS REVIEW — New Driver Application",
    fromName: "Essex Black Cab website",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { capacity: "5" } });

  const onSubmit = async (values) => {
    const file = values.licenceFile?.[0];
    // File submissions always use the multipart path with the attachment —
    // an application is never accepted without its licence document.
    await submit(
      {
        "Review status": "NEEDS REVIEW — manual approval required before receiving any work",
        "Driver name": values.name,
        "Mobile number": values.phone,
        "Essex home area": values.essexArea,
        "TfL Green Badge licence number": values.badgeNumber,
        "Licence expiry date": values.licenceExpiry,
        "Licence evidence": file ? `Attached (${file.name})` : "MISSING — do not approve",
        "Usual Essex → London times": values.morningTimes,
        "Usual London → Essex times": values.eveningTimes,
        "Preferred London areas": values.londonAreas,
        "Vehicle capacity": values.capacity,
        "Agreed to referral terms": "Yes — £20 referral fee per completed referred journey",
        email: values.email,
        replyto: values.email,
      },
      undefined,
      file,
      "Licence document"
    );
  };

  if (status === "success") {
    return (
      <div
        data-testid="driver-confirmation"
        className="border border-[rgba(212,175,55,0.4)] bg-[#111111] p-8"
      >
        <CheckCircle2 className="h-10 w-10 text-[#D4AF37]" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-semibold text-white">Application received</h3>
        <p className="mt-3 leading-relaxed text-[#A1A1AA]">
          Thank you for applying to join the Essex Black Cab driver network. Every application
          is reviewed personally and manually — there is no automatic approval. Your licence
          document has been received with your application for verification.
        </p>
        <p className="mt-3 leading-relaxed text-[#A1A1AA]">
          If your application is suitable, we will contact you to verify your TfL Green Badge
          licence and explain the referral arrangements before any journeys are offered.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="driver-form" className="space-y-6">
      <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

      <Field label="Full name" error={errors.name?.message} htmlFor="d-name">
        <input id="d-name" data-testid="input-driver-name" autoComplete="name" className={inputCls} {...register("name")} />
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Mobile number" error={errors.phone?.message} htmlFor="d-phone">
          <input id="d-phone" data-testid="input-driver-phone" type="tel" autoComplete="tel" className={inputCls} placeholder="07xxx xxxxxx" {...register("phone")} />
        </Field>
        <Field label="Email address" error={errors.email?.message} htmlFor="d-email">
          <input id="d-email" data-testid="input-driver-email" type="email" autoComplete="email" className={inputCls} {...register("email")} />
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="TfL Green Badge licence number" error={errors.badgeNumber?.message} htmlFor="d-badge">
          <input id="d-badge" data-testid="input-driver-badge" className={inputCls} placeholder="Required — shown on your badge" {...register("badgeNumber")} />
        </Field>
        <Field label="Licence expiry date" error={errors.licenceExpiry?.message} htmlFor="d-expiry">
          <input id="d-expiry" data-testid="input-driver-expiry" type="date" className={`${inputCls} [color-scheme:dark]`} {...register("licenceExpiry")} />
        </Field>
      </div>

      <Field
        label="Licence photo or PDF (required)"
        error={errors.licenceFile?.message}
        htmlFor="d-licence"
      >
        <input
          id="d-licence"
          data-testid="input-driver-licence"
          type="file"
          accept=".jpg,.jpeg,.png,.heic,.heif,.pdf,image/jpeg,image/png,image/heic,image/heif,application/pdf"
          className={`${inputCls} file:mr-4 file:border-0 file:bg-[#D4AF37] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-black`}
          {...register("licenceFile")}
        />
        <p className="mt-2 text-xs leading-relaxed text-[#71717A]">
          A clear photo of your current TfL taxi driver licence (JPG, PNG, HEIC or PDF, max
          10MB). It is sent privately to the owner for manual verification — never published or
          shared with other drivers.
        </p>
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Essex home town / general area" error={errors.essexArea?.message} htmlFor="d-area">
          <input id="d-area" data-testid="input-driver-area" className={inputCls} placeholder="e.g. Great Dunmow" {...register("essexArea")} />
        </Field>
        <Field label="Vehicle passenger capacity" error={errors.capacity?.message} htmlFor="d-capacity">
          <select id="d-capacity" data-testid="input-driver-capacity" className={`${inputCls} bg-[#050505]`} {...register("capacity")}>
            {["4", "5", "6"].map((n) => (
              <option key={n} value={n}>{n} passengers</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Usual Essex → London travel times" error={errors.morningTimes?.message} htmlFor="d-morning">
          <input id="d-morning" data-testid="input-driver-morning" className={inputCls} placeholder="e.g. Weekday mornings, 05:30–08:00" {...register("morningTimes")} />
        </Field>
        <Field label="Usual London → Essex travel times" error={errors.eveningTimes?.message} htmlFor="d-evening">
          <input id="d-evening" data-testid="input-driver-evening" className={inputCls} placeholder="e.g. Evenings, 18:00–23:00" {...register("eveningTimes")} />
        </Field>
      </div>

      <Field label="Preferred London working areas" error={errors.londonAreas?.message} htmlFor="d-london">
        <input id="d-london" data-testid="input-driver-london" className={inputCls} placeholder="e.g. City, Liverpool Street, Canary Wharf" {...register("londonAreas")} />
      </Field>

      <div className="space-y-4">
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]">
          <input type="checkbox" data-testid="input-driver-terms" className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" {...register("terms")} />
          <span>
            I agree to the{" "}
            <a href="/driver-terms" className="text-[#D4AF37] underline-offset-4 hover:underline">
              Driver Referral Terms
            </a>
            : for each referred journey I complete, the passenger pays me the full £100 inside
            my cab, and I pay Essex Black Cab a separate £20 referral fee by bank transfer
            within 24 hours of completion. I retain £80.
          </span>
        </label>
        {errors.terms && <p role="alert" className={errCls} data-testid="error-driver-terms">{errors.terms.message}</p>}

        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]">
          <input type="checkbox" data-testid="input-driver-privacy" className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" {...register("privacy")} />
          <span>
            I have read and acknowledge the{" "}
            <a href="/privacy-policy" className="text-[#D4AF37] underline-offset-4 hover:underline">
              privacy notice
            </a>
            . My details will be used only to process this application and will never be published.
          </span>
        </label>
        {errors.privacy && <p role="alert" className={errCls} data-testid="error-driver-privacy">{errors.privacy.message}</p>}
      </div>

      {status === "error" && (
        <p role="alert" data-testid="driver-submit-error" className="border border-red-900 bg-red-950/40 p-4 text-sm text-red-300">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        data-testid="driver-submit-button"
        disabled={status === "sending"}
        className="focus-gold w-full border border-[#D4AF37] bg-[#D4AF37] px-6 py-4 text-base font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "sending" ? "Sending your application…" : "Apply to join the driver network"}
      </button>
    </form>
  );
}
